import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import { useWindowSize } from '@hooks/useWindowSize';
import { mdiArrowLeft } from '@mdi/js';
import Icon from '@mdi/react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Skeleton } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetOffersSpecialist, useMonitoringOffersStore } from 'services/monitoring-offers';
import { useUsersStore } from 'services/users';
import { chatService } from '../../../services/chat/chatService';
import { MonitoringOngoingCard } from '../components/Card/MonitoringOngoingCard';
import { Empty } from '../components/Empty';
import { http, ApiResponse } from '@config/api';
import { useSnackbarStore } from '@components/shared/Snackbar/store';

// Define response type for the delete API
interface DeleteMonitoringResponse {
  success: boolean;
  data: any;
  error: string;
}

const styles = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		boxShadow: 3,
		position: 'relative',
		bgcolor: 'white',
		borderRadius: 3,
	},
};

// Get SPECIALIST_ID from environment variable
const ADMIN_SPECIALIST_ID = process.env.REACT_APP_SPECIALIST_ID;

export const MonitoringOngoingSpecialist = () => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const size = useWindowSize();
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useUsersStore();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

	// Admin functionality state
	const [selectedItems, setSelectedItems] = useState<string[]>([]);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	// Always show admin features for testing
	const isAdmin = true; // user?.user_id === ADMIN_SPECIALIST_ID;

	// Track if we're currently in a fetch operation to prevent duplicates
	const isFetchingRef = useRef(false);
	const initialFetchDoneRef = useRef(false);

	// Get filter from URL query parameter (even though toggle is disabled, use URL to be consistent)
	const queryParams = new URLSearchParams(location.search);
	const filterParam = queryParams.get('filter');
	const showCompleted = filterParam === 'completed';

	// Single consolidated loading state - only for offers, not channels
	const [isLoading, setIsLoading] = useState(true);
	// Track loading state per monitoring ID
	const [loadingChats, setLoadingChats] = useState<Record<string, boolean>>({});

	const { refetch: fetchOffersSpecialist, isLoading: isLoadingOffers } = useGetOffersSpecialist();
	const { ongoingOffers } = useMonitoringOffersStore();

	// Simplified fetch function that only handles offers loading
	const fetchData = useCallback(async () => {
		// Prevent concurrent fetches
		if (isFetchingRef.current) {
			return;
		}

		try {
			isFetchingRef.current = true;
			setIsLoading(true);

			// Fetch specialist offers
			await fetchOffersSpecialist();

			// Wait briefly to ensure store is updated
			await new Promise((resolve) => setTimeout(resolve, 50));
		} finally {
			setIsLoading(false);
			isFetchingRef.current = false;
			initialFetchDoneRef.current = true;
		}
	}, [fetchOffersSpecialist]);

	// Only fetch offers data on mount
	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Handle chat button click with on-demand channel fetching
	const handleChat = useCallback(
		async (monitoringId: string) => {
			// Set loading state for this specific monitoring ID
			setLoadingChats(prev => ({ ...prev, [monitoringId]: true }));
			
			try {
				const channel = await chatService.GetChannelByMonitoringId(monitoringId);
				if (channel?.id) {
					navigateWithLanguage(`/specialist/chats?channelId=${channel.id}`);
				} else {
					console.error('No channel ID found for monitoring offer');
				}
			} catch (error) {
				console.error('Error fetching channel:', error);
			} finally {
				// Clear loading state for this specific monitoring ID
				setLoadingChats(prev => ({ ...prev, [monitoringId]: false }));
			}
		},
		[navigateWithLanguage]
	);

	// Function to check if a specific chat is loading
	const isChatLoading = useCallback((monitoringId: string) => {
		return !!loadingChats[monitoringId];
	}, [loadingChats]);

	// Admin functionality handlers
	const handleCheckboxChange = useCallback((monitoringId: string, event: React.ChangeEvent<HTMLInputElement>) => {
		event.stopPropagation();
		setSelectedItems(prev => {
			if (prev.includes(monitoringId)) {
				return prev.filter(id => id !== monitoringId);
			} else {
				return [...prev, monitoringId];
			}
		});
	}, []);

	const handleDeleteClick = useCallback(() => {
		if (selectedItems.length > 0) {
			setIsDeleteDialogOpen(true);
		}
	}, [selectedItems]);

	const handleDeleteConfirm = useCallback(async () => {
		if (selectedItems.length === 0) return;
		
		setIsDeleting(true);
		try {
			// Construct URL with multiple monitoring-id parameters
			const queryParams = selectedItems.map(id => `monitoring-id=${id}`).join('&');
			const url = `admin-delete-monitoring-projects?${queryParams}`;
			
			const response = await http.post<ApiResponse<DeleteMonitoringResponse>>('monitoring/', url);
			
			if (response.data && response.data.success) {
				// Show success message
				showSnackbar('Success', selectedItems.length === 1 
					? t('monitoringProjectDeletedSuccessfully') 
					: t('multipleMonitoringProjectsDeletedSuccessfully', { count: selectedItems.length }), 
					'success');
				
				// Refresh the data after successful deletion
				await fetchData();
				setSelectedItems([]);
			} else {
				const errorMessage = response.data && typeof response.data === 'object' && 'error' in response.data 
					? String(response.data.error) 
					: 'Unknown error';
				console.error('Failed to delete monitoring projects:', errorMessage);
				showSnackbar('Error', `Failed to delete monitoring projects: ${errorMessage}`, 'error');
			}
		} catch (error) {
			console.error('Error deleting monitoring projects:', error);
			showSnackbar('Error', 'An error occurred while deleting monitoring projects', 'error');
		} finally {
			setIsDeleting(false);
			setIsDeleteDialogOpen(false);
		}
	}, [selectedItems, fetchData, showSnackbar, t]);

	const handleDeleteCancel = useCallback(() => {
		setIsDeleteDialogOpen(false);
	}, []);

	usePageTitle(t('monitoring'));
	useAppbarTitle('monitoring');

	const handleToggleCompleted = () => {
		// Button is disabled but keep the URL pattern consistent with farmer view
		const newFilter = !showCompleted ? 'completed' : 'ongoing';
		navigate({ pathname: location.pathname, search: `?filter=${newFilter}` });
	};

	return (
		<Box
			sx={{
				...styles.container,
				minHeight: size.width > 768 ? '100%' : 'auto',
			}}
		>
			<div className="flex justify-center items-center">
				<div className="absolute left-[20px] top-[50px]">
					<Button
						className="w-[120px]"
						type="submit"
						onClick={() => navigate(-1)}
						variant="text"
						sx={{ backgroundColor: 'white', color: 'black' }}
						startIcon={<Icon path={mdiArrowLeft} size={1} />}
					>
						{t('back')}
					</Button>
				</div>
				<button
					className="absolute right-[20px] top-[50px] flex items-center justify-center gap-2 cursor-pointer opacity-70"
					onClick={handleToggleCompleted}
					disabled
				>
					<CheckCircleOutlineIcon sx={{ color: showCompleted ? 'success.main' : 'inherit' }} />
					<span
						className={`text-base font-bold underline ${showCompleted ? 'text-green-600' : 'text-dark'}`}
					>
						{t('completed')}
					</span>
				</button>
				<h2 className="md:text-2xl font-bold mb-6 mt-5">
					{t(
						showCompleted
							? 'requestsAndOffers.completedProjectsTitle'
							: 'requestsAndOffers.ongoingProjectsTitle'
					)}
				</h2>
			</div>

			{/* Admin Delete Button */}
			{isAdmin && !isLoading && ongoingOffers.length > 0 && (
				<div className="px-[20px] pb-2 flex justify-end">
					<Button
						variant="contained"
						color="error"
						startIcon={<DeleteIcon />}
						disabled={selectedItems.length === 0}
						onClick={handleDeleteClick}
						sx={{ 
							fontWeight: 'bold',
							py: { xs: 1.5, sm: 1 },
							px: { xs: 3, sm: 2 },
							my: 2
						}}
					>
						{t('delete')} ({selectedItems.length})
					</Button>
				</div>
			)}

			<div className="p-[20px]">
				{isLoading ? (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
						{Array.from({ length: 6 }).map((_, index) => (
							<Box key={index} sx={{ padding: 2, boxShadow: 3, borderRadius: 3, bgcolor: 'white' }}>
								<Skeleton variant="rectangular" height={150} />
								<Skeleton variant="text" sx={{ marginTop: 1, height: 30 }} />
								<Skeleton variant="text" width="80%" />
							</Box>
						))}
					</div>
				) : ongoingOffers.length === 0 ? (
					<Empty
						title={
							showCompleted
								? t('requestsAndOffers.noCompletedProjectsYet')
								: t('requestsAndOffers.noOngoingProjectsYet')
						}
						buttonTitle={t('monitoringService')}
						route="/specialist/monitoring/offers"
						onClick={() => navigateWithLanguage('/specialist/monitoring/offers')}
					/>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
						{ongoingOffers.map((data: any, index: any) => (
							<div key={index}>
								<MonitoringOngoingCard
									data={data}
									onChat={handleChat}
									chatLoading={isChatLoading(data?.id)}
									isAdmin={isAdmin}
									isSelected={selectedItems.includes(data?.id)}
									onCheckboxChange={handleCheckboxChange}
								/>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Confirmation Dialog */}
			<Dialog
				open={isDeleteDialogOpen}
				onClose={!isDeleting ? handleDeleteCancel : undefined}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					{t('deleteConfirmation')}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{selectedItems.length === 1 
							? t('deleteMonitoringProjectConfirmation')
							: t('deleteMultipleMonitoringProjectsConfirmation', { count: selectedItems.length })}
						<br />
						<strong>{t('thisActionCannotBeUndone')}</strong>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteCancel} disabled={isDeleting}>
						{t('cancel')}
					</Button>
					<Button 
						onClick={handleDeleteConfirm} 
						color="error" 
						variant="contained"
						disabled={isDeleting}
						startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
						autoFocus
					>
						{isDeleting ? t('deleting') : t('delete')}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
